import React, { useEffect, useState, useCallback } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  Dialog, 
  Title, 
  Select, 
  Option, 
  BusyIndicator, 
  Input, 
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const DistrictList = () => {
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'code',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    active: true,
    companyId: '',
    districtName: '',
    addresses: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [companies, setCompanies] = useState([]);
  const { autoT } = useAutoTranslate();

  // Trae TODOS los distritos paginando en el servidor (size grande) y deja la
  // paginación/búsqueda del lado del cliente; así no dependemos de que el backend
  // devuelva `totalCount` correcto y se ven todos los registros (no solo 10).
  const fetchDistricts = useCallback(async () => {
    setLoading(true);
    try {
      const serverPageSize = 100;
      let page = 1;
      const all = [];
      for (;;) {
        const url = `districts?page=${page}&size=${serverPageSize}`;
        const response = await axiosInstance.get(url);
        if (response.data.records.code !== 'SUCCESS') {
          setError(response.data.records.message);
          break;
        }
        const batch = Array.isArray(response.data.records.data) ? response.data.records.data : [];
        all.push(...batch);

        const total =
          typeof response.data.totalCount === 'number' && response.data.totalCount >= 0
            ? response.data.totalCount
            : null;

        if (total != null && all.length >= total) break;
        if (batch.length === 0) break;
        if (batch.length < serverPageSize) break;
        page += 1;
        if (page > 500) break;
      }
      setData(all);
    } catch (err) {
      setError('Error al cargar los distritos: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosInstance.get('companies?page=1&size=100');
      if (response.data.records.code === "SUCCESS") {
        setCompanies(response.data.records.data);
      }
    } catch (err) {
      console.error('Error al cargar las compañías:', err);
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
    fetchCompanies();
  }, [fetchDistricts, fetchCompanies]);

  useEffect(() => {
    let filtered = [...data];
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(district => 
        district.code?.toLowerCase().includes(lowerCaseSearchTerm) ||
        district.districtName?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    // Ordenar los datos
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  // Paginación del lado del cliente sobre los datos ya filtrados/ordenados.
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const pagedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Si al filtrar/eliminar la página actual queda fuera de rango, la corregimos.
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Función para manejar el clic en una fila
  const handleRowClick = (event) => {
    // Solo permitir editar si tiene el permiso de actualización
    if (!hasPermission('district', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const district = detail.row.original;
      setSelectedDistrict(district);
      
      // Inicializar el formulario con los datos del distrito seleccionado
      setFormData({
        code: district.code || '',
        active: district.active !== undefined ? district.active : true,
        companyId: district.companyId || '',
        districtName: district.districtName || '',
        addresses: district.addresses || []
      });
      
      // Mostrar el diálogo de edición
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleCreateClick = () => {
    // Solo permitir crear distritos si tiene el permiso de creación
    if (!hasPermission('district', 'create')) {
      return;
    }
    
    setFormData({
      code: '',
      active: true,
      companyId: '',
      districtName: '',
      addresses: []
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleEditClick = (district) => {
    // Solo permitir editar distritos si tiene el permiso de actualización
    if (!hasPermission('district', 'update')) {
      return;
    }
    
    setSelectedDistrict(district);
    setFormData({
      code: district.code || '',
      active: district.active !== undefined ? district.active : true,
      companyId: district.companyId || '',
      districtName: district.districtName || '',
      addresses: district.addresses || []
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleDeleteClick = (district) => {
    // Solo permitir eliminar distritos si tiene el permiso de eliminación
    if (!hasPermission('district', 'delete')) {
      return;
    }
    
    setSelectedDistrict(district);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDistrict) return;

    try {
      // El backend responde 204 No Content (sin body) en éxito; axios no lo trata
      // como error, así que llegar aquí sin excepción ya significa que se eliminó.
      await axiosInstance.delete(`districts/${selectedDistrict.id}`);
      setError(null);
      setShowDeleteConfirm(false);
      await fetchDistricts();
    } catch (err) {
      // En error real (p. ej. distrito en uso por FK) el backend manda 400 con el
      // motivo en `data`/`message`. Mostramos ese motivo en vez del genérico.
      const msg =
        err.response?.data?.message ??
        err.response?.data?.data ??
        err.message ??
        'No se pudo eliminar el distrito';
      setError('Error al eliminar el distrito: ' + msg);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar el error del campo cuando el usuario comienza a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute('value');
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleToggleActive = () => {
    setFormData(prev => ({
      ...prev,
      active: !prev.active
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.code) {
      errors.code = 'El código es obligatorio';
    }
    
    if (!formData.districtName) {
      errors.districtName = 'El nombre del distrito es obligatorio';
    }
    
    if (!formData.companyId) {
      errors.companyId = 'La compañía es obligatoria';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const payload = {
        ...formData,
        createdDate: new Date().toISOString()
      };
      
      const response = await axiosInstance.post('districts', payload);
      if (response.status === 201 || response.status === 200) {
        fetchDistricts();
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el distrito: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el distrito: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedDistrict) return;
    
    try {
      const payload = {
        ...formData,
        modifiedDate: new Date().toISOString()
      };
            
      const response = await axiosInstance.put(`districts/${selectedDistrict.id}`, payload);
      if (response.status === 200) {
        fetchDistricts();
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el distrito: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el distrito: ' + (err.message || 'Error desconocido'));
    }
  };

  // Resuelve el nombre de la compañía tolerando distintas formas del backend:
  // `companyId` / `company_id` (UUID) o un objeto `company` anidado con id/name.
  const getCompanyName = (district) => {
    if (!district) return 'Desconocida';
    if (district.company && typeof district.company === 'object') {
      const name = district.company.name ?? district.company.fullName;
      if (name) return name;
    }
    const companyId = district.companyId ?? district.company_id ?? district.company;
    const company = companies.find((c) => c.id === companyId);
    return company ? company.name : 'Desconocida';
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const newAddresses = [...prev.addresses];
      newAddresses[index] = {
        ...newAddresses[index],
        [field]: value
      };
      return {
        ...prev,
        addresses: newAddresses
      };
    });
  };

  const handleAddAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          street: '',
          city: '',
          state: '',
          zip: '',
          country: ''
        }
      ]
    }));
  };

  const handleRemoveAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  const columns = [
    {
      Header: 'Código',
      accessor: 'code',
      headerProps: {
        onClick: () => handleSort('code')
      },
      className: sortConfig.sortBy === 'code' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Nombre',
      accessor: 'districtName',
      headerProps: {
        onClick: () => handleSort('districtName')
      },
      className: sortConfig.sortBy === 'districtName' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Compañía',
      accessor: 'companyId',
      Cell: ({ row }) => getCompanyName(row.original),
      headerProps: {
        onClick: () => handleSort('companyId')
      },
      className: sortConfig.sortBy === 'companyId' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Estado',
      accessor: 'active',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon 
            name={value ? "status-positive" : "status-negative"} 
            style={{ marginRight: '8px', color: value ? 'green' : 'red' }} 
          />
          <Text>{value ? 'Activo' : 'Inactivo'}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort('active')
      },
      className: sortConfig.sortBy === 'active' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Fecha Creación',
      accessor: 'createdDate',
      Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '',
      headerProps: {
        onClick: () => handleSort('createdDate')
      },
      className: sortConfig.sortBy === 'createdDate' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('district', 'update') && (
            <Button 
              icon="edit" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
          {hasPermission('district', 'delete') && (
            <Button 
              icon="delete" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de distritos')}</Title>
        
        {error && (
          <div style={{ color: 'red', margin: '1rem 0' }}>
            {error}
          </div>
        )}
        
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Input
                placeholder={autoT('Buscar distrito...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: '250px' }}
              />
            </div>
          }
          endContent={
            hasPermission('district', 'create') && (
              <Button 
                icon="add" 
                design="Emphasized"
                onClick={handleCreateClick}
              >
                {autoT('Nuevo Distrito')}
              </Button>
            )
          }
        />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={pagedData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('district', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                icon="navigation-left-arrow" 
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button 
                icon="navigation-right-arrow" 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>
      
      {/* Dialog para crear distrito */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        header={
          <Bar
            startContent={
              <Title level="H4">{autoT('Crear Nuevo Distrito')}</Title>
            }
          />
        }
        footer={
          <Bar
            endContent={
              <>
                <Button design="Emphasized" onClick={handleCreateSubmit}>
                  {autoT('Crear')}
                </Button>
                <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText="Información del Distrito"
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}

          <FormGroup titleText="Información Básica">
            <FormItem labelContent={<Label>Código</Label>}>
              <Input 
                name="code" 
                value={formData.code}
                onChange={handleInputChange}
                valueState={formErrors.code ? "Error" : "None"}
                valueStateMessage={formErrors.code}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Nombre del Distrito</Label>}>
              <Input 
                name="districtName" 
                value={formData.districtName}
                onChange={handleInputChange}
                valueState={formErrors.districtName ? "Error" : "None"}
                valueStateMessage={formErrors.districtName}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Compañía</Label>}>
              <Select 
                name="companyId" 
                onChange={handleSelectChange}
                value={formData.companyId}
                valueState={formErrors.companyId ? "Error" : "None"}
                valueStateMessage={formErrors.companyId}
                required
              >
                <Option value="">Seleccione una compañía</Option>
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Direcciones">
            {formData.addresses.map((address, index) => (
              <div key={index} style={{ 
                marginBottom: '1rem', 
                padding: '1rem', 
                border: '1px solid #e5e5e5', 
                borderRadius: '4px' 
              }}>
                <FormItem labelContent={<Label>Calle</Label>}>
                  <Input 
                    name={`addresses.${index}.street`}
                    value={address.street}
                    onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Ciudad</Label>}>
                  <Input 
                    name={`addresses.${index}.city`}
                    value={address.city}
                    onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Estado/Provincia</Label>}>
                  <Input 
                    name={`addresses.${index}.state`}
                    value={address.state}
                    onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Código Postal</Label>}>
                  <Input 
                    name={`addresses.${index}.zip`}
                    value={address.zip}
                    onChange={(e) => handleAddressChange(index, 'zip', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>País</Label>}>
                  <Input 
                    name={`addresses.${index}.country`}
                    value={address.country}
                    onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                    required
                  />
                </FormItem>
                <Button 
                  design="Negative"
                  onClick={() => handleRemoveAddress(index)}
                  style={{ marginTop: '0.5rem' }}
                >
                  Eliminar Dirección
                </Button>
              </div>
            ))}
            {/* <Button 
              design="Emphasized"
              onClick={handleAddAddress}
              style={{ marginTop: '1rem' }}
            >
              Agregar Dirección
            </Button> */}
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog para editar distrito */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        header={
          <Bar
            startContent={
              <Title level="H4">{`${autoT('Editar Distrito')}: ${selectedDistrict?.districtName}`}</Title>
            }
          />
        }
        footer={
          <Bar
            startContent={
              hasPermission('district', 'delete') && (
                <Button
                  design="Negative"
                  onClick={() => {
                    setShowEditDialog(false);
                    handleDeleteClick(selectedDistrict);
                  }}
                  icon="delete"
                >
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>
                  {autoT('Guardar')}
                </Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText="Información del Distrito"
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}

          <FormGroup titleText="Información Básica">
            <FormItem labelContent={<Label>Código</Label>}>
              <Input 
                name="code" 
                value={formData.code}
                onChange={handleInputChange}
                valueState={formErrors.code ? "Error" : "None"}
                valueStateMessage={formErrors.code}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Nombre del Distrito</Label>}>
              <Input 
                name="districtName" 
                value={formData.districtName}
                onChange={handleInputChange}
                valueState={formErrors.districtName ? "Error" : "None"}
                valueStateMessage={formErrors.districtName}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Compañía</Label>}>
              <Select 
                name="companyId" 
                onChange={handleSelectChange}
                value={formData.companyId}
                valueState={formErrors.companyId ? "Error" : "None"}
                valueStateMessage={formErrors.companyId}
                required
              >
                <Option value="">Seleccione una compañía</Option>
                {companies.map((company) => (
                  <Option key={company.id} value={company.id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Direcciones">
            {formData.addresses.map((address, index) => (
              <div key={index} style={{ 
                marginBottom: '1rem', 
                padding: '1rem', 
                border: '1px solid #e5e5e5', 
                borderRadius: '4px' 
              }}>
                <FormItem labelContent={<Label>Calle</Label>}>
                  <Input 
                    name={`addresses.${index}.street`}
                    value={address.street}
                    onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Ciudad</Label>}>
                  <Input 
                    name={`addresses.${index}.city`}
                    value={address.city}
                    onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Estado/Provincia</Label>}>
                  <Input 
                    name={`addresses.${index}.state`}
                    value={address.state}
                    onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>Código Postal</Label>}>
                  <Input 
                    name={`addresses.${index}.zip`}
                    value={address.zip}
                    onChange={(e) => handleAddressChange(index, 'zip', e.target.value)}
                    required
                  />
                </FormItem>
                <FormItem labelContent={<Label>País</Label>}>
                  <Input 
                    name={`addresses.${index}.country`}
                    value={address.country}
                    onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                    required
                  />
                </FormItem>
                <Button 
                  design="Negative"
                  onClick={() => handleRemoveAddress(index)}
                  style={{ marginTop: '0.5rem' }}
                >
                  Eliminar Dirección
                </Button>
              </div>
            ))}
            {/* <Button 
              design="Emphasized"
              onClick={handleAddAddress}
              style={{ marginTop: '1rem' }}
            >
              Agregar Dirección
            </Button> */}
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(action) => {
          if (action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        titleText={autoT('Confirmar eliminación')}
        type={MessageBoxType.Warning}
        actions={[MessageBoxAction.OK, MessageBoxAction.Cancel]}
      >
        {autoT('¿Está seguro que desea eliminar el distrito')} <strong>{selectedDistrict?.code}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </>
  );
};

export default withPermission(DistrictList, 'district', 'listView');
